import {
  CHAT_REPORT_CATEGORIES,
  type ChatMessage,
  type ChatReportCategory,
  MAX_CHAT_NOTE_LENGTH,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useState } from "react";

import { toUserFacingChatError, useReportChatMessage } from "../../lib/chat.js";
import { ErrorBanner } from "../ErrorBanner.js";

const CATEGORY_LABELS: Record<ChatReportCategory, string> = {
  HARASSING: "Harassment",
  INAPPROPRIATE: "Inappropriate content",
  SPAM: "Spam",
  SUSPICIOUS: "Suspicious activity",
};

/** Reports one message to the site's staff: a category and an optional note. */
export function ReportChatDialog({
  message,
  onClose,
}: {
  /** The message being reported; null keeps the dialog closed. */
  message: ChatMessage | null;
  onClose: () => void;
}) {
  const report = useReportChatMessage(message?.roomId);
  const [category, setCategory] = useState<ChatReportCategory | null>(null);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const close = () => {
    setCategory(null);
    setDescription("");
    setError(null);
    setDone(false);
    report.reset();
    onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message || !category) return;
    setError(null);
    report.mutate(
      { messageId: message.messageId, category, description: description.trim() || undefined },
      { onSuccess: () => setDone(true), onError: (err) => setError(toUserFacingChatError(err)) },
    );
  };

  return (
    <Dialog open={message !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Report message</DialogTitle>
          <DialogDescription>
            {message ? `From ${message.displayName}. Staff will review it.` : null}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <>
            <p className="text-sm">Thanks. The report was sent to staff.</p>
            <DialogFooter>
              <DialogClose render={<Button type="button" />}>Done</DialogClose>
            </DialogFooter>
          </>
        ) : (
          <form className="grid gap-3" onSubmit={submit}>
            <div className="grid gap-1.5">
              <Label htmlFor="chat-report-category">Reason</Label>
              <Select
                value={category}
                items={CATEGORY_LABELS}
                onValueChange={(value) => setCategory((value as ChatReportCategory | null) ?? null)}
              >
                <SelectTrigger id="chat-report-category" className="w-full" aria-label="Reason">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {CHAT_REPORT_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="chat-report-description">Details (optional)</Label>
              <Textarea
                id="chat-report-description"
                value={description}
                maxLength={MAX_CHAT_NOTE_LENGTH}
                rows={3}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <DialogFooter>
              <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
              <Button type="submit" disabled={!category || report.isPending}>
                {report.isPending ? "Sending…" : "Send report"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
